import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";
import { formatDateDisplay } from "../lib/utils";
import { toast } from "sonner";
import { Clock, ChevronLeft, Lock, Tag, CheckCircle2, X } from "lucide-react";

export default function PreOrderCheckoutPage() {
  const { preOrderItems, clearPreOrderCart } = useCart();
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    shipping_address: "", city: "", state: "", pincode: "",
    comments: "",
  });
  const [couponInput, setCouponInput] = useState("");
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  useEffect(() => {
    if (preOrderItems.length === 0) nav("/cart");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (preOrderItems.length === 0) return null;

  const item = preOrderItems[0];
  const subtotal = item.price * item.quantity;
  const shipping = subtotal > 4999 ? 0 : 100;
  const discount = couponApplied?.discount || 0;
  const finalTotal = Math.max(0, subtotal + shipping - discount);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    try {
      const r = await api.post("/coupons/validate", {
        code: couponInput.trim(),
        subtotal,
        product_ids: [item.product_id],
      });
      setCouponApplied({ code: r.data.coupon.code, discount: r.data.discount });
      toast.success(`Coupon applied! You save ₹${r.data.discount.toLocaleString()}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid coupon");
      setCouponApplied(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponApplied(null);
    setCouponInput("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // 1. Create Razorpay order — no DB write yet
      const rzRes = await api.post("/payments/create-order", {
        order_type: "preorder",
        amount: finalTotal,
        product_id: item.product_id,
        product_name: item.name,
        quantity: item.quantity,
        release_date: item.release_date || null,
        coupon_code: couponApplied?.code || null,
        comments: form.comments,
        ...form,
      });

      // 2. Open Razorpay checkout
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: rzRes.data.amount,
        currency: "INR",
        name: "GamesTrade",
        description: "Pre-Order Payment",
        order_id: rzRes.data.razorpay_order_id,
        prefill: {
          name: form.customer_name,
          email: form.customer_email,
          contact: form.customer_phone,
        },
        theme: { color: "#C9A84C" },
        handler: async (response) => {
          try {
            // 3. Verify + create preorder in DB only on successful payment
            const verifyRes = await api.post("/payments/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              order_type: "preorder",
              product_id: item.product_id,
              product_name: item.name,
              quantity: item.quantity,
              release_date: item.release_date || null,
              coupon_code: couponApplied?.code || null,
              comments: form.comments,
              ...form,
            });
            clearPreOrderCart();
            toast.success("Payment successful! Pre-Order #" + verifyRes.data.order_number);
            nav(`/preorder-success/${verifyRes.data.order_id}`);
          } catch {
            toast.error("Payment received but verification failed. Contact support.");
          }
        },
        modal: {
          ondismiss: () => {
            setSubmitting(false);
            toast.error("Payment cancelled");
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        setSubmitting(false);
        toast.error("Payment failed. Please try again.");
      });
      rzp.open();
    } catch (err) {
      setSubmitting(false);
      toast.error(err?.response?.data?.detail || "Failed to initiate payment");
    }
  };

  return (
    <div className="site-container py-10">
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      <Link to="/cart" className="text-xs text-slate-400 hover:text-gold uppercase tracking-wider inline-flex items-center gap-1 mb-6">
        <ChevronLeft className="w-3 h-3" /> Back to cart
      </Link>
      <h1 className="text-4xl font-black uppercase tracking-tighter mb-8">Pre-<span className="text-gold">Order</span> Checkout</h1>

      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_360px] gap-10">
        <div className="space-y-6">
          <div className="bg-navy-700 rounded-xl p-6 neon-border">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.2em] text-gold mb-4">Contact</h2>
            <div className="space-y-3">
              <Field name="customer_name" label="Full Name" value={form.customer_name} onChange={onChange} required />
              <div className="grid sm:grid-cols-2 gap-3">
                <Field name="customer_email" type="email" label="Email" value={form.customer_email} onChange={onChange} required />
                <Field name="customer_phone" label="Phone" value={form.customer_phone} onChange={onChange} required />
              </div>
            </div>
          </div>

          <div className="bg-navy-700 rounded-xl p-6 neon-border">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.2em] text-gold mb-4">Shipping Address</h2>
            <div className="space-y-3">
              <Field name="shipping_address" label="Address" value={form.shipping_address} onChange={onChange} required />
              <div className="grid sm:grid-cols-3 gap-3">
                <Field name="city" label="City" value={form.city} onChange={onChange} required />
                <Field name="state" label="State" value={form.state} onChange={onChange} required />
                <Field name="pincode" label="Pincode" value={form.pincode} onChange={onChange} required />
              </div>
            </div>
          </div>

          <div className="bg-navy-700 rounded-xl p-6 neon-border">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.2em] text-gold mb-4">Payment</h2>
            <div className="flex items-center gap-3 p-4 bg-navy-800 rounded-lg border border-gold/40">
              <Lock className="w-4 h-4 text-gold shrink-0" />
              <div>
                <div className="font-bold text-sm uppercase tracking-wider">Razorpay — UPI / Card / Net Banking</div>
                <div className="text-[11px] text-slate-400 mt-0.5">Secured by Razorpay. You'll complete payment in the next step.</div>
              </div>
            </div>
          </div>

          <div className="bg-navy-700 rounded-xl p-6 neon-border">
            <h2 className="text-sm font-extrabold uppercase tracking-[0.2em] text-gold mb-4">Notes</h2>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Comments / Special Requests</span>
              <textarea
                name="comments"
                value={form.comments}
                onChange={onChange}
                rows={3}
                placeholder="Any preferences, deposit info, etc."
                className="w-full mt-1 bg-navy-800 border border-gold/20 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-gold/60 resize-none"
              />
            </label>
          </div>
        </div>

        <aside className="bg-navy-700 rounded-xl p-6 border border-gold/40 h-fit">
          <h2 className="text-sm font-extrabold uppercase tracking-[0.2em] text-gold mb-5">Pre-Order Summary</h2>

          <div className="flex gap-3 mb-5">
            {item.image && <img src={item.image} alt="" className="w-16 h-16 object-cover rounded-lg shrink-0" />}
            <div className="min-w-0">
              <span className="bg-gold text-navy-900 text-[9px] font-black uppercase tracking-[0.15em] px-2 py-0.5 rounded">PRE-ORDER</span>
              <div className="font-bold text-sm mt-1 line-clamp-2">{item.name}</div>
              <div className="text-gold font-black text-sm">₹{item.price.toLocaleString()} × {item.quantity}</div>
              {item.release_date && (
                <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                  <Clock className="w-3 h-3 shrink-0" />
                  Releases {formatDateDisplay(item.release_date)}
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-gold/20 pt-4">
            {couponApplied ? (
              <div className="flex items-center justify-between bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2 text-emerald-400 text-xs font-bold">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span className="font-mono">{couponApplied.code}</span>
                  <span className="text-emerald-300">— ₹{couponApplied.discount.toLocaleString()} off</span>
                </div>
                <button type="button" onClick={removeCoupon} className="text-slate-400 hover:text-rose-400 transition">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Tag className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gold/60" />
                  <input
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyCoupon())}
                    placeholder="Coupon code"
                    className="w-full bg-navy-800 border border-gold/20 rounded-md pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-gold/60"
                  />
                </div>
                <button
                  type="button"
                  onClick={applyCoupon}
                  disabled={couponLoading || !couponInput.trim()}
                  className="bg-navy-600 border border-gold/30 text-gold font-bold text-xs px-4 py-2 rounded-md hover:border-gold transition disabled:opacity-40"
                >
                  {couponLoading ? "…" : "Apply"}
                </button>
              </div>
            )}
          </div>

          <div className="border-t border-gold/20 pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-400">Subtotal</span><span>₹{subtotal.toLocaleString()}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Shipping</span><span>{shipping === 0 ? "FREE" : `₹${shipping}`}</span></div>
            {discount > 0 && (
              <div className="flex justify-between text-emerald-400"><span>Discount</span><span>−₹{discount.toLocaleString()}</span></div>
            )}
            <div className="flex justify-between text-base border-t border-gold/20 pt-2 mt-2">
              <span className="font-extrabold uppercase">Total</span>
              <span className="font-black text-gold text-xl">₹{finalTotal.toLocaleString()}</span>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="block w-full text-center mt-5 bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-sm py-3.5 rounded-full hover:bg-gold-light btn-glow transition-all disabled:opacity-50"
          >
            {submitting ? "Opening Payment..." : "Pay & Confirm Pre-Order"}
          </button>
        </aside>
      </form>
    </div>
  );
}

function Field({ label, name, ...rest }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">{label}</span>
      <input
        name={name}
        {...rest}
        className="w-full mt-1 bg-navy-800 border border-gold/20 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-gold/60"
      />
    </label>
  );
}
